<!-- Copyright 2019-2026 ForgeRock AS. All Rights Reserved

Use of this code requires a commercial software license with ForgeRock AS
or with one of its affiliates. All use shall be exclusively subject
to such license between the licensee and ForgeRock AS. -->
<template>
  <span>
    <FrLayout
      :menu-items="menuItems"
      :realm="realmName"
      :realm-aliases="realmAliases"
      :realm-menu-items="realmMenuItems"
      :tenant-menu-items="tenantMenuItems"
      :release-info="$store.state.tenantReleaseInfo"
      :is-fraas="$store.state.isFraas"
      :is-internal-user="amAdmin">
      <RouterView v-slot="{ Component }">
        <Transition
          name="fade"
          mode="out-in">
          <Component
            :is="Component"
            :key="$route.fullPath" />
        </Transition>
      </RouterView>
    </FrLayout>
    <FrCreateRealmModal />
    <FrSwitchRealmModal />
    <FrAdminInviteModal />
    <FrLoadPromotedAppDataModal />
    <span
      v-if="buildNumber"
      class="d-none">
      {{ buildNumber }}
    </span>
  </span>
</template>

<script>
import FrLayout from '@forgerock/platform-shared/src/components/Layout/';
import LoginMixin from '@forgerock/platform-shared/src/mixins/LoginMixin';
import NotificationMixin from '@forgerock/platform-shared/src/mixins/NotificationMixin';
import ValidationRules from '@forgerock/platform-shared/src/utils/validationRules';
import { mapState } from 'pinia';
import { useUserStore } from '@forgerock/platform-shared/src/stores/user';
import { useCustomDomainsStore } from '@/stores/customDomains';
import FrCreateRealmModal from './views/Realm/CreateModal';
import FrSwitchRealmModal from './views/Realm/SwitchModal';
import FrAdminInviteModal from './views/ManagedIdentities/AdminInviteModal';
import FrLoadPromotedAppDataModal from './components/LoadPromotedAppDataModal';
import i18n from '@/i18n';
import './scss/main.scss';
import { getTenantInfo, getTenantReleaseInfo } from '@/api/TenantInfoApi';
import { getLocalLockState, getPairedLockStateForReceiving, getProvisionalReport } from '@/api/FraasPromotionApi';
import emailHash from '@/utils/emailHash';
import { getChangeCountFromReport, getTenantNameFromURL } from '@/utils/promotions';
import { getCustomDomains } from '@/services/customDomainService';
import loadPendoScript from '@/services/pendoLoader';

/**
 * @description Controlling component for admin, allows for listing available resources and connects to the create, delete and edit features.
 *
 * @fires GET config/managed - Obtain which managed objects are deployed to this user
 */
export default {
  name: 'App',
  mixins: [
    LoginMixin,
    NotificationMixin,
  ],
  components: {
    FrCreateRealmModal,
    FrSwitchRealmModal,
    FrAdminInviteModal,
    FrLoadPromotedAppDataModal,
    FrLayout,
  },
  data() {
    return {
      buildNumber: process.env.VUE_APP_BUILD_NUMBER,
    };
  },
  methods: {
    /**
     * Updates the value of an item in a menu item's sub menu (dropdown)
     *
     * @param {string} parentName - display name of the parent menu
     * @param {string} name - display name of the child menu item
     * @param {string} property - property name to update
     * @param {string} value - new value for the property
     */
    setSubMenuItemPropertyByDisplayName(parentName, name, property, value) {
      const parentIndex = this.menuItems.findIndex((item) => (item.displayName && this.$t(item.displayName) === parentName));
      if (parentIndex === -1) {
        // parent menu not found
        return;
      }
      const index = this.menuItems[parentIndex].subItems.findIndex((item) => this.$t(item.displayName) === name);
      this.menuItems[parentIndex].subItems[index][property] = value;
    },
    /**
     * Updates the value of a property in a menu item
     *
     * @param {string} name - display name of the child menu item
     * @param {string} property - property name to update
     * @param {string} value - new value for the property
     */
    setMenuItemPropertyByDisplayName(name, property, value) {
      const index = this.menuItems.findIndex((item) => (item.displayName && this.$t(item.displayName) === name));
      this.menuItems[index][property] = value;
    },
    async loadCustomDomains() {
      const realmName = (this.$store.state.realm === 'root') ? '/' : this.$store.state.realm;
      const customDomains = await getCustomDomains(realmName);
      useCustomDomainsStore().customDomains = customDomains;
    },
    loadPendo() {
      loadPendoScript()
        .then((pendo) => {
          pendo.initialize({
            visitor: {
              id: emailHash(this.email),
            },
            account: {
              id: this.$store.state.promotionTenantInfo.currentName,
              featureGovernance: this.$store.state.SharedStore.governanceEnabled,
              featureWorkforce: this.$store.state.SharedStore.workforceEnabled,
              featureAutoAccess: this.$store.state.SharedStore.autoAccessEnabled,
            },
            parentAccount: {
              id: this.$store.state.salesforceId,
            },
            // Prevent API call interference
            excludeEvents: ['xhr', 'fetch'],
          });
        })
        .catch(() => {
          // Pendo analytics should not disrupt the app
        });
    },
  },
  created() {
    // add vee-validate rules
    const rules = ValidationRules.getRules(i18n);
    ValidationRules.extendRules(rules);
  },
  mounted() {
    if (!this.$store.state.SharedStore.idmOnly) {
      this.loadCustomDomains();
    }
    if (this.$store.state.isFraas) {
      getTenantReleaseInfo().then(({ data }) => {
        this.$store.commit('setTenantReleaseInfo', data);
      }).catch((error) => {
        this.showErrorMessage(error, this.$t('tenantSettings.details.errors.errorRetrievingReleaseInfo'));
      });

      getTenantInfo().then(({ data }) => {
        this.$store.commit('setTenantInfo', data);

        if (this.$store.state.SharedStore.fraasPromotionUrl) {
          const { state: { SharedStore: { fraasPromotionIngressUrl, fraasPromotionEgressUrl }, tenant } } = this.$store;
          const currentName = getTenantNameFromURL(tenant);
          const lowerName = fraasPromotionIngressUrl ? getTenantNameFromURL(fraasPromotionIngressUrl) : '';
          const upperName = fraasPromotionEgressUrl ? getTenantNameFromURL(fraasPromotionEgressUrl) : '';
          // Set the tier types
          this.$store.commit('setPromotionTenantInfo', {
            currentName,
            currentTier: data.promotionTierInfo?.tierDisplayName,
            upperName,
            upperTier: data.promotionTierInfo?.upperTierDisplayName,
            lowerName,
            lowerTier: data.promotionTierInfo?.lowerTierDisplayName,
          });
        }
      }).then(() => {
        const { currentTier } = this.$store.state.promotionTenantInfo;
        // Load promotion data if we're on a tenant which can promote config
        if (this.$store.state.SharedStore.fraasPromotionEgressUrl) {
          // Load the current pending promotion changes to display in the top right menu
          getProvisionalReport()
            .then(({ data: { report } }) => {
              this.$store.commit('setTenantPromotableChanges', getChangeCountFromReport(report));
            });

          // Check if this tenant is locked to push a promotion - if so, route to the promotion view
          if (currentTier === 'staging' || currentTier === 'uat') {
            // If we're on a staging/UAT tenant, check the lock state of lower and upper (can be done without extra auth).
            // If only upper is locked, it must be to promote to upper environment. Show the promotion view.
            getPairedLockStateForReceiving()
              .then(({ data: { lowerEnv: { state: lowerLockState }, upperEnv: { state: currentLockState } } }) => {
                if (currentLockState === 'locked' && lowerLockState !== 'locked') {
                  this.$router.push('/promotion');
                }
              });
          }
          if (currentTier === 'development') {
            // If we're on a dev/lowest tier tenant, check the lock state.
            // If it is locked, it must be to promote to staging or UAT. Show the promotion view.
            getLocalLockState()
              .then(({ data: { result } }) => {
                if (result === 'locked') {
                  this.$router.push('/promotion');
                }
              });
          }
        }
        // Check for workforce promotion data
        if (this.$store.state.SharedStore.fraasPromotionIngressUrl
          && this.$store.state.SharedStore.workforceEnabled
          && this.$store.state.SharedStore.promoteAppsViaApi === false
          && this.$store.state.tenantInfo.config_promotion_done === true) {
          // Show the load app data modal
          this.$root.$emit('bv::show::modal', 'loadPromotedAppDataModal');
        }
      }).catch((error) => {
        this.showErrorMessage(error, this.$t('tenantSettings.details.errors.errorRetrievingTenantRegion'));
      });
    }
  },
  watch: {
    '$store.state.realm': function stateRealmChanged(val) {
      this.displayNotification('success', this.$t('realm.switchSuccessful', { realmName: val }));

      // Update native consoles AM link
      this.setSubMenuItemPropertyByDisplayName(
        this.$t('sideMenu.nativeConsoles'),
        this.$t('sideMenu.accessManagement'),
        'url',
        `${this.$store.getters.amPrefix}/dashboard`,
      );

      // update realm aliases/custom domains
      this.loadCustomDomains();
    },
    '$store.state.loadPendo': function stateLoadPendoChanged(loadPendo) {
      if (loadPendo) {
        this.loadPendo();
      }
    },
  },
  computed: {
    ...mapState(useUserStore, ['amAdmin', 'email']),
    realmName() {
      return this.$store.state.realm === 'root' ? this.$t('realm.topLevelRealm') : this.$store.state.realm;
    },
    realmAliases() {
      const aliases = useCustomDomainsStore().customDomains;
      if (aliases.length) {
        return this.$tc('realm.aliasString', aliases.length, { firstAlias: aliases[0], remainingAliases: aliases.length - 1 });
      }
      return `/${this.$store.state.realm}`;
    },
    menuItems() {
      return this.$store.state.menuItems;
    },
    realmMenuItems() {
      return this.$store.state.realmMenuItems;
    },
    tenantMenuItems() {
      return this.$store.state.tenantMenuItems;
    },
  },
};
</script>
